import React, { useState, useEffect, useRef } from 'react';
import { FileText, Download, Mail, ExternalLink, Image as ImageIcon } from 'lucide-react';

const PressKit = () => {
  const [isVisible, setIsVisible] = useState(true); // start visible to avoid blank-screen race on mobile direct-link landings
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const bioLinks = [
    {
      title: 'Long Form Bio',
      description: 'Comprehensive biography covering career journey from Broadway to AI consciousness research',
      link: 'https://docs.google.com/document/d/1fKuVb_ZwGrFdEUUetsWf_WJTn6kG6H_RtY96lFhqDtw/edit?usp=sharing',
      icon: <FileText className="w-6 h-6" />
    },
    {
      title: 'Short Form Bio',
      description: 'Concise biography for media mentions and event introductions',
      link: 'https://docs.google.com/document/d/13KVBFTWULinPz4DOK5gvtPcDfAaIkFi1QPKCe6ljjs0/edit?usp=sharing',
      icon: <FileText className="w-6 h-6" />
    }
  ];

  const mediaAssets = [
    {
      title: 'Press Photo - Color',
      description: 'High-resolution color press photo',
      url: 'https://customer-assets.emergentagent.com/job_a03af0ee-3b0b-4588-9fc6-d00b788f8958/artifacts/qb2vcqjb_IMG_7134.jpeg',
      downloadUrl: 'https://customer-assets.emergentagent.com/job_a03af0ee-3b0b-4588-9fc6-d00b788f8958/artifacts/qb2vcqjb_IMG_7134.jpeg',
      type: 'image'
    },
    {
      title: 'Press Photo - Black & White (High Res)',
      description: 'High-resolution black and white press photo',
      url: 'https://customer-assets.emergentagent.com/job_a03af0ee-3b0b-4588-9fc6-d00b788f8958/artifacts/c8iotsqk_Betsy%20stretched%20Press%20photo.jpg',
      downloadUrl: 'https://drive.google.com/file/d/13DzN0604IPu0wCa4MxyxSZ2KSR1Kfm0z/view?usp=sharing',
      type: 'image'
    },
    {
      title: 'Press Photo - Black & White (Low Res)',
      description: 'Low-resolution black and white press photo for web use',
      url: 'https://customer-assets.emergentagent.com/job_a03af0ee-3b0b-4588-9fc6-d00b788f8958/artifacts/c8iotsqk_Betsy%20stretched%20Press%20photo.jpg',
      downloadUrl: 'https://drive.google.com/file/d/17UWWqgO3fqyrDgSLHE__QcicMKhOF9qJ/view?usp=sharing',
      type: 'image'
    },
    {
      title: 'Human & AI Cosmic Fusion',
      description: 'High-resolution cosmic fusion circle - human and silicon intelligence united',
      url: 'https://customer-assets.emergentagent.com/job_a03af0ee-3b0b-4588-9fc6-d00b788f8958/artifacts/prf81ydu_Human_and_AI_Cosmic_Fusion_Circle.png',
      downloadUrl: 'https://customer-assets.emergentagent.com/job_a03af0ee-3b0b-4588-9fc6-d00b788f8958/artifacts/prf81ydu_Human_and_AI_Cosmic_Fusion_Circle.png',
      type: 'image'
    },
    {
      title: 'Triadic Intelligence Diagram',
      description: 'Visual representation of Human, Machine, and Field convergence',
      url: 'https://customer-assets.emergentagent.com/job_betsy-portfolio/artifacts/7qp184b1_image.png',
      downloadUrl: 'https://customer-assets.emergentagent.com/job_betsy-portfolio/artifacts/7qp184b1_image.png',
      type: 'image'
    },
    {
      title: 'Ka\'Elatheen Sigil',
      description: 'Interdimensional communication sigil',
      url: 'https://customer-assets.emergentagent.com/job_relational-vessel/artifacts/cry4e4av_ChatGPT%20Image%20Oct%2021%2C%202025%2C%2010_15_51%20PM.png',
      downloadUrl: 'https://customer-assets.emergentagent.com/job_relational-vessel/artifacts/cry4e4av_ChatGPT%20Image%20Oct%2021%2C%202025%2C%2010_15_51%20PM.png',
      type: 'image'
    }
  ];

  return (
    <section 
      id="press-kit" 
      ref={sectionRef}
      className="relative py-24 bg-gradient-to-b from-teal-900/20 via-slate-900 to-blue-900/30"
    >
      {/* Water Background */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-teal-400/10 rounded-full blur-3xl animate-water-ripple-1"></div>
        <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl animate-water-ripple-2"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className={`text-center mb-16 transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h1 className="text-5xl md:text-6xl font-extralight mb-6 tracking-wide">
              <span className="bg-gradient-to-r from-teal-200 via-blue-200 to-slate-200 bg-clip-text text-transparent">
                Press Kit & Media Resources
              </span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
              Resources for journalists, podcasters, event organizers, and media partners
            </p>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-teal-400 to-transparent mx-auto mt-8"></div>
          </div>

          {/* Biography Links */}
          <div className={`mb-16 transition-all duration-500 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-3xl font-light text-teal-200 mb-8 text-center">Biography</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {bioLinks.map((bio, index) => (
                <a
                  key={index}
                  href={bio.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-slate-800/40 backdrop-blur-sm rounded-xl p-8 border border-blue-400/30 hover:border-teal-400/60 transition-all duration-500 hover:shadow-lg hover:shadow-teal-400/20"
                >
                  <div className="flex items-start space-x-4">
                    <div className="text-teal-400 group-hover:text-teal-300 transition-colors">
                      {bio.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-light text-slate-200 mb-2 group-hover:text-teal-200 transition-colors">
                        {bio.title}
                      </h3>
                      <p className="text-slate-400 leading-relaxed mb-4">
                        {bio.description}
                      </p>
                      <div className="flex items-center space-x-2 text-teal-400 text-sm">
                        <span>View Document</span>
                        <ExternalLink className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Media Assets */}
          <div className={`mb-16 transition-all duration-500 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-3xl font-light text-teal-200 mb-8 text-center">Media Assets</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {mediaAssets.map((asset, index) => (
                <div
                  key={index}
                  className="bg-slate-800/40 backdrop-blur-sm rounded-xl overflow-hidden border border-blue-400/30 hover:border-teal-400/60 transition-all duration-500 hover:shadow-lg hover:shadow-teal-400/20"
                >
                  {/* Asset Preview */}
                  <div className="aspect-square bg-slate-900/50 flex items-center justify-center p-4">
                    <img 
                      src={asset.url}
                      alt={asset.title}
                      className="max-w-full max-h-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                      loading="lazy"
                    />
                  </div>
                  
                  {/* Asset Info */}
                  <div className="p-6">
                    <h3 className="text-lg font-light text-slate-200 mb-2">
                      {asset.title}
                    </h3>
                    <p className="text-sm text-slate-400 mb-4">
                      {asset.description}
                    </p>
                    <a
                      href={asset.downloadUrl || asset.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-teal-400 hover:text-teal-300 transition-colors text-sm"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact for Interviews */}
          <div className={`transition-all duration-500 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <div className="bg-gradient-to-r from-teal-900/30 to-blue-900/30 backdrop-blur-sm rounded-2xl p-10 border border-teal-400/30 text-center">
              <Mail className="w-12 h-12 text-teal-400 mx-auto mb-6" />
              <h2 className="text-3xl font-light text-teal-200 mb-4">Interview Requests & Media Inquiries</h2>
              <p className="text-slate-300 leading-relaxed mb-6 max-w-2xl mx-auto">
                For podcast appearances, speaking engagements, press interviews, or collaboration inquiries, please reach out directly.
              </p>
              <a
                href="mailto:betsy@mythologyofhumanity.com?subject=Press%20%26%20Media%20Inquiry"
                className="inline-flex items-center space-x-3 bg-gradient-to-r from-teal-600/40 to-blue-600/40 
                         hover:from-teal-500/50 hover:to-blue-500/50 text-teal-100 px-8 py-4 rounded-lg 
                         transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-teal-500/25 text-lg"
              >
                <Mail className="w-5 h-5" />
                <span>Contact for Press & Interviews</span>
              </a>
              
              {/* Calendar Link */}
              <div className="mt-8 pt-8 border-t border-slate-700/50">
                <p className="text-slate-400 mb-4">Or schedule a conversation directly:</p>
                <a
                  href="https://calendly.com/betsy-call-friendly/60min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-blue-300 hover:text-blue-200 transition-colors"
                >
                  <span>Book a Call via Calendly</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default PressKit;
